import React, { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { LuMail, LuGift, LuArrowLeft } from 'react-icons/lu'
import { FaRegUserCircle } from 'react-icons/fa'
import { CiLock } from 'react-icons/ci'
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa6'
import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js'
import CryptoJS from 'crypto-js'
import { Box, Typography, Link } from '@mui/material'
import { toast } from '../core/lib/toastAlert'
import Config from '../core/config'
import {
  makeRequestWithErrorMessage,
} from '../core/services/v1/request'
import CaptchaModal from '../Component/Captcha/CaptchaModal'
import { Spinner } from 'react-bootstrap'
import { showEmail } from '../core/helper/date-format'
import OtpInput from 'react-otp-input'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import phone from 'phone'
import { handleGoogleSignIn } from '../Component/Auth/GoogleAuth'
import TelegramLogin from '../Component/Auth/TelegramAuth'
import Splash from '../Component/Splash/Splash'

const SECRET_KEY = Config.ENCKEY
/* =========================
   Three.js scene (reused/adapted)
========================= */
function useUniticLeftScene(mountRef) {
  useEffect(() => {
    const mount = mountRef.current
    if (!mount) return

    // ---------- Renderer ----------
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setClearColor(0x000000, 0)
    renderer.outputColorSpace = THREE.SRGBColorSpace
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = 1.05
    mount.appendChild(renderer.domElement)

    // ---------- Scene ----------
    const scene = new THREE.Scene()
    scene.fog = new THREE.Fog(0x020617, 8.0, 18.0)

    // ---------- Camera ----------
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 60)
    camera.position.set(0.2, 0.2, 10.8)

    // ---------- Environment (deferred for faster first paint; final look unchanged) ----------
    let pmrem = null
    const setupEnvironment = () => {
      if (scene.environment) return
      pmrem = new THREE.PMREMGenerator(renderer)
      const envTex = pmrem.fromScene(
        new RoomEnvironment(renderer),
        0.05,
      ).texture
      scene.environment = envTex
    }

    // ---------- Lights ----------
    const ambient = new THREE.AmbientLight(0xffffff, 0.22)
    scene.add(ambient)

    const key = new THREE.DirectionalLight(0xffffff, 1.15)
    key.position.set(5, 3.2, 7)
    scene.add(key)

    const fill = new THREE.DirectionalLight(0x1da094, 0.55)
    fill.position.set(-6, 1.8, 3)
    scene.add(fill)

    const rim = new THREE.PointLight(0x1da094, 0.9, 20, 2)
    rim.position.set(-3.5, 1.2, -2)
    scene.add(rim)

    // ---------- Root group ----------
    const root = new THREE.Group()
    root.position.set(-1.25, -0.65, 0)
    root.scale.set(0.65, 0.65, 0.65)
    scene.add(root)

    // ---------- Materials ----------
    const ringMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color('#070c12'),
      metalness: 0.55,
      roughness: 0.32,
      clearcoat: 0.35,
      clearcoatRoughness: 0.5,
      envMapIntensity: 0.55,
    })

    const ringMat2 = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color('#0a1018'),
      metalness: 0.45,
      roughness: 0.55,
      clearcoat: 0.2,
      clearcoatRoughness: 0.65,
      envMapIntensity: 0.35,
      transparent: true,
      opacity: 0.95,
    })

    const blobMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color('#03070c'),
      metalness: 0.18,
      roughness: 0.42,
      emissive: new THREE.Color('#072a26'),
      emissiveIntensity: 0.25,
      envMapIntensity: 0.35,
      clearcoat: 0.2,
      clearcoatRoughness: 0.5,
    })

    // ---------- Geometry (Similar style but maybe slight variation if desired, keeping consistent for now) ----------
    const torus = new THREE.Mesh(
      new THREE.TorusGeometry(3.15, 0.2, 24, 320),
      ringMat,
    )
    torus.rotation.set(0.25, 0.25, 0.12)
    root.add(torus)

    const ring2 = new THREE.Mesh(
      new THREE.TorusGeometry(2.55, 0.06, 22, 280),
      ringMat2,
    )
    ring2.scale.set(1.35, 0.56, 1.0)
    ring2.rotation.set(-0.7, 0.12, -0.42)
    root.add(ring2)

    // Small outer frame ring
    const frameRing = new THREE.Mesh(
      new THREE.TorusGeometry(3.55, 0.02, 14, 220),
      new THREE.MeshStandardMaterial({
        color: new THREE.Color('#0b1722'),
        metalness: 0.2,
        roughness: 0.9,
        transparent: true,
        opacity: 0.35,
      }),
    )
    frameRing.rotation.set(0.2, 0.2, 0.05)
    root.add(frameRing)

    // Blocks
    const blockGeo = new THREE.BoxGeometry(0.42, 0.26, 0.2)
    const blockMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color('#060b11'),
      metalness: 0.5,
      roughness: 0.4,
      clearcoat: 0.25,
      clearcoatRoughness: 0.7,
      envMapIntensity: 0.45,
    })

    function addBlock(angle, radius = 3.15, z = 0.0) {
      const b = new THREE.Mesh(blockGeo, blockMat)
      const x = Math.cos(angle) * radius
      const y = Math.sin(angle) * radius
      b.position.set(x, y, z)
      b.lookAt(0, 0, 0)
      b.rotation.z += 0.55
      root.add(b)
      return b
    }
    addBlock(2.35, 3.15, 0.02)
    addBlock(5.05, 3.15, -0.02)

    // Add some extra blocks for SignUp "building" vibe
    addBlock(1.2, 3.15, 0.05)

    // Central Blob (now a perfect Sphere)
    const blobGeo = new THREE.SphereGeometry(1.85, 64, 64)
    const blob = new THREE.Mesh(blobGeo, blobMat)
    blob.position.set(0, 0, 0)
    root.add(blob)

    // No vertex displacement shader anymore (perfect sphere)
    blobMat.needsUpdate = true

    // Glow particles
    const spriteTex = new THREE.TextureLoader().load(
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAAAQCAYAAABAfUpcAAAACXBIWXMAAAsSAAALEgHS3X78AAABHUlEQVR4nO2XwQ3CMAxF5xQkQJpA0gSgCSRNQJIApAkgTQDJH6l3a9bO2s8gk0q8oVxv4R4o2k0F+8gK2w1o5p6dXyV8oGmQd5zqE2dWJtXkQWn0wYhZcHnQz2jBf2p0bQxJ0wQqjzJx0m0xj3mA0q1Kc3hQdZpQm5cQxgk3zYB7cGmO4kG9wq1WvJbJz1o7m3wC8d3Y1Q8xYcGm3f4mBq9m1g8Qw8q3oF+qv8b9wJ9i2mQm1u8u9o0n4lB3+g5yQqQ0Zb4e3w8Y0xqg4c6m9oWf5oGm9m6YVjz0a8w8Q8H2g3f8wAAAABJRU5ErkJggg==',
    )
    spriteTex.colorSpace = THREE.SRGBColorSpace
    const glowMat = new THREE.SpriteMaterial({
      map: spriteTex,
      color: new THREE.Color('#bff7f0'),
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
    const p1 = new THREE.Sprite(glowMat.clone())
    p1.scale.set(0.35, 0.35, 0.35)
    p1.position.set(1.05, 0.65, 0.75)
    root.add(p1)
    const p2 = new THREE.Sprite(glowMat.clone())
    p2.scale.set(0.22, 0.22, 0.22)
    p2.position.set(1.25, 0.35, 0.62)
    root.add(p2)

    // Postprocessing (deferred; preserves same visual outcome)
    let composer = null
    let bloom = null
    const setupComposer = () => {
      if (composer) return
      composer = new EffectComposer(renderer)
      composer.addPass(new RenderPass(scene, camera))
      bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.55, 0.8, 0.9)
      composer.addPass(bloom)
    }

    // Resize
    const resize = () => {
      const w = mount.clientWidth
      const h = mount.clientHeight
      camera.aspect = w / h
      camera.updateProjectionMatrix()
      renderer.setSize(w, h, false)
      if (composer) {
        composer.setSize(w, h)
        bloom?.setSize(w, h)
      }
    }
    resize()
    const onResize = () => resize()
    window.addEventListener('resize', onResize)
    let ro = null
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(() => resize())
      ro.observe(mount)
    }

    // Animate
    const clock = new THREE.Clock()
    let raf = 0
    const animate = () => {
      const t = clock.getElapsedTime()

      root.rotation.z = t * 0.06
      torus.rotation.y = 0.25 + t * 0.07
      ring2.rotation.z = -0.42 - t * 0.05

      camera.position.x = 0.2 + Math.sin(t * 0.18) * 0.07
      camera.position.y = 0.2 + Math.sin(t * 0.32) * 0.05
      camera.lookAt(-1.2, -0.45, 0)

      p1.material.opacity = 0.45 + (Math.sin(t * 2.1) * 0.5 + 0.5) * 0.5
      p2.material.opacity = 0.35 + (Math.sin(t * 1.7 + 1.1) * 0.5 + 0.5) * 0.55

      if (composer) {
        composer.render()
      } else {
        renderer.render(scene, camera)
      }
      raf = requestAnimationFrame(animate)
    }
    // Kick first frame quickly, then attach heavy bits
    raf = requestAnimationFrame(() => {
      animate()
      setTimeout(() => {
        setupEnvironment()
        setupComposer()
        resize()
      }, 0)
    })

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
      ro && ro.disconnect()
      composer?.dispose?.()
      pmrem?.dispose?.()
      renderer.dispose()
      if (renderer.domElement?.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement)
      }
    }
  }, [mountRef])
}

export default function SignUp() {
  const threeMountRef = useRef(null)
  useUniticLeftScene(threeMountRef)
  let captchaValue = useRef(null)
  const [isLoading, setisLoading] = useState(false)
  const [successPopup, setSuccessPopup] = useState(false)
  const [openCaptcha, setOpenCaptcha] = useState(false)
  const navigate = useNavigate()
  const [eye, setEye] = useState({ pass: false, coPass: false })
  const [phoneno, setphoneno] = useState('')
  const location = useLocation()
  const {
    from,
    idToken,
    email,
    step,
    gAuth,
    teleDataToken,
    telegramId,
    username,
    teleAuth,
  } = location.state || {}
  const [formData, setFormData] = useState({
    email: '',
    referralCode: '',
    captcha_value: '',
    otp: '',
    phoneno: '',
    password: '',
    confirmPassword: '',
    username: '',
    agreeTerms: false,
    step: 1,
    gAuth: false,
    idToken: '',
    teleDataToken: {},
    telegramId: '',
    teleAuth: false,
  })
  const handleOpenCaptcha = () => setOpenCaptcha(true)
  const handleCloseCaptcha = () => {
    if (captchaValue.current?.getValue()) {
      setOpenCaptcha(false)
      register()
    }
  }
  const [timer_resendOtp, setTimer_resendOtp] = useState(0)

  useEffect(() => {
    timer_resendOtp > 0 &&
      setTimeout(() => setTimer_resendOtp(timer_resendOtp - 1), 1000)
  }, [timer_resendOtp])
  useEffect(() => {
    if (from === 'login' && idToken) {
      setFormData((prev) => ({
        ...prev,
        idToken: idToken,
        email: email,
        gAuth: gAuth,
        step: step,
      }))
    }
    if (from === 'login' && telegramId && teleDataToken) {
      setFormData((prev) => ({
        ...prev,
        teleDataToken: teleDataToken,
        telegramId: telegramId,
        username: username,
        teleAuth: teleAuth,
        step: step,
      }))
    }
  }, [
    from,
    idToken,
    email,
    gAuth,
    step,
    teleDataToken,
    telegramId,
    username,
    teleAuth,
  ])
  const register = async (isResend = 0) => {
    setisLoading(true)
    const payload = Object.assign(formData, {
      isResend: isResend,
      captcha_value: captchaValue.current?.getValue() || '',
      phoneno: '+' + phoneno,
    })
    if (payload?.step === 1 && !payload?.teleAuth && !payload?.captcha_value) {
      setisLoading(false)
      toast({ type: 'error', message: 'Please verify captcha' })
      return false
    }
    captchaValue.current?.reset()
    const encryptedData = CryptoJS.AES.encrypt(
      JSON.stringify(payload),
      SECRET_KEY,
    ).toString()
    const params = {
      url: `${Config.V1_API_URL}user/register-web`,
      method: 'POST',
      data: { data: encryptedData },
    }
    const response = await makeRequestWithErrorMessage(params)
    setisLoading(false)
    let type = 'error'
    if (response.status) {
      type = 'success'
      setFormData((prev) => ({ ...prev, step: response.data?.step }))
      setTimer_resendOtp(Config.timer.resendOtp)
      if (!response.data?.type || response.data?.type !== 1) {
        toast({ type, message: response.message })
      }
      if (response.data?.type === 1) {
        setSuccessPopup(true)
      }
    } else {
      toast({ type, message: response.message })
    }
  }
  const handleSubmit = async (e, isResend = 0) => {
    e?.preventDefault()
    if (formData?.step === 1 && !formData?.teleAuth) {
      return handleOpenCaptcha()
    }
    if (formData?.step === 3) {
      setFormData((prev) => ({ ...prev, phoneno: '+' + phoneno }))
      let phn = '+' + phoneno
      let type = 'error'
      if (phn == '' || typeof phn == undefined) {
        return toast({ type, message: 'Enter your phone number' })
      }
      const phoneDetail = phone(phn, { country: '' })
      if (phoneDetail.isValid === false) {
        return toast({
          type,
          message: 'Invalid phone number, Please enter correct phone number',
        })
      }
    }
    return register(isResend)
  }
  const signWithGoogle = async () => {
    try {
      setisLoading(true)
      const gAuth = await handleGoogleSignIn('register-web')
      if (gAuth) {
        let type = 'error'
        if (gAuth?.status) {
          type = 'success'
          toast({ type, message: gAuth?.message })
          if (gAuth.data?.type === 1) {
            setisLoading(false)
            return navigate('/login')
          }
          if (gAuth.data?.step) {
            setFormData((prev) => ({
              ...prev,
              idToken: gAuth?.data?.idToken,
              email: gAuth?.data?.email,
              gAuth: gAuth?.data?.isCreated,
              step: gAuth.data?.step,
            }))
          }
        }

        toast({ type, message: gAuth?.message })
      } else {
        toast({ type: 'error', message: 'Google Sign-up Failed' })
      }
      setisLoading(false)
    }
    catch (error) {
      toast({ type: 'error', message: 'Google Sign-up Failed' })
      setisLoading(false)
    }
  }
  const handleAuthTele = async (responseData) => {
    setisLoading(true)
    if (responseData) {
      let type = 'error'
      if (responseData?.status) {
        type = 'success'
        toast({ type, message: responseData?.message })
        if (responseData.data?.type === 1) {
          setisLoading(false)
          return navigate('/login')
        }
        if (responseData.data?.step) {
          setFormData((prev) => ({
            ...prev,
            teleDataToken: responseData?.data?.teleDataToken,
            telegramId: responseData?.data?.telegramId,
            username: responseData?.data?.username,
            teleAuth: responseData?.data?.isCreated,
            step: responseData.data?.step,
          }))
        }
      }

      toast({ type, message: responseData?.message })
    } else {
      toast({ type: 'error', message: 'Telegram Sign-up Failed' })
    }
    setisLoading(false)
  }
  return (
    <>
      <h1 className='visually-hidden'>Sign Up</h1>
      <Helmet>
        <title>Create Account - Unitic Exchange</title>
        <meta
          name='description'
          content='Join millions of traders on Unitic Exchange'
        />
        <link rel='canonical' href='https://uniticexchange.com/register' />
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          bgcolor: '#0C0E13',
          color: 'common.white',
          overflow: 'hidden',
        }}
      >
        {/* LEFT SECTION (Three.js) */}
        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            width: '50%',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#0E1116',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60%',
              height: '60%',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(29, 160, 148, 0.08) 0%, rgba(14, 17, 22, 0) 70%)',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
          <Link
            component={RouterLink}
            to='/'
            underline='none'
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: 'rgba(148,163,184,0.9)',
              fontSize: 14,
              '&:hover': { color: 'common.white' },
              zIndex: 20,
            }}
          >
            <LuArrowLeft style={{ fontSize: 14 }} />
            Back to Home
          </Link>

          {/* Canvas */}
          <Box
            ref={threeMountRef}
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 3,
              '& canvas': {
                width: '100% !important',
                height: '100% !important',
                display: 'block',
                pointerEvents: 'none',
              },
            }}
          />

          {/* Vignette */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 4,
              background:
                'radial-gradient(1200px 800px at 50% 50%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.35) 85%, rgba(0,0,0,0.55) 100%)',
              pointerEvents: 'none',
            }}
          />

          {/* Bottom Text */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 56,
              left: 0,
              right: 0,
              px: 8,
              textAlign: 'center',
              zIndex: 10,
            }}
          >
            <Typography
              sx={{ fontSize: 40, fontWeight: 800, letterSpacing: -0.6 }}
            >
              Start Your Crypto Journey
            </Typography>
            <Typography
              sx={{
                mt: 1.5,
                color: 'rgba(148,163,184,0.9)',
                fontSize: 16,
                maxWidth: 560,
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              Join millions of traders and unlock the future of finance with our
              secure platform.
            </Typography>
          </Box>
        </Box>

        {/* RIGHT SECTION (Form) */}
        <div
          className='d-flex align-items-center justify-content-center position-relative overflow-hidden'
          style={{
            flex: 1,
            backgroundColor: '#0E1116',
            padding: '48px 16px',
          }}
        >
          {/* Mobile background effects */}
          <div
            className='position-absolute top-0 start-0 w-100 h-100 d-lg-none'
            style={{ overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}
          >
            <div
              style={{
                position: 'absolute',
                top: '33%',
                right: '-25%',
                width: '600px',
                height: '600px',
                background: 'rgba(29, 160, 148, 0.2)',
                borderRadius: '50%',
                filter: 'blur(120px)',
                animation: 'pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '33%',
                left: '-25%',
                width: '500px',
                height: '500px',
                background: 'rgba(29, 160, 148, 0.1)',
                borderRadius: '50%',
                filter: 'blur(100px)',
                animation: 'pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                animationDelay: '1s',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage:
                  'linear-gradient(rgba(20,184,166,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(20,184,166,0.03) 1px, transparent 1px)',
                backgroundSize: '50px 50px',
              }}
            />
          </div>
          <style>
            {`
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.5; }
              }
            `}
          </style>

          {/* Mobile Back Button */}
          <RouterLink
            to='/'
            className='d-lg-none position-absolute text-decoration-none d-flex align-items-center gap-2'
            style={{
              top: '16px',
              left: '16px',
              color: 'rgba(148,163,184,0.9)',
              zIndex: 20,
            }}
          >
            <LuArrowLeft /> Back to Home
          </RouterLink>

          <div
            className='w-100'
            style={{ maxWidth: '448px', position: 'relative', zIndex: 10 }}
          >
            {/* Logo */}
            <div className='text-center mb-5 pb-1'>
              <RouterLink to='/' className='text-decoration-none'>
                <img src='/logo.png' alt='Unitic' className='img-fluid img-size' />
              </RouterLink>
            </div>

            {/* Card */}
            <div
              className='rounded-4'
              style={{
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(20px)',
                padding: '32px 24px',
              }}
            >
              <div className='text-center mb-4'>
                <h2
                  className='fw-bold text-white mb-2'
                  style={{ fontSize: '24px' }}
                >
                  {formData?.step === 1
                    ? 'Create Account'
                    : formData?.step === 2
                      ? 'Verify OTP'
                      : formData?.step === 3
                        ? 'Fill Up Details'
                        : 'Create Account'}
                </h2>
                <p
                  style={{
                    color: 'rgba(148,163,184,0.9)',
                    fontSize: '14px',
                    margin: 0,
                  }}
                >
                  Join millions of traders on the world's leading platform
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                {formData?.step === 1 && !formData?.teleAuth ? (
                  <>
                    {/* Email */}
                    <div className='mb-3'>
                      <label
                        className='d-block mb-2 fw-bold'
                        style={{
                          fontSize: '12px',
                          color: 'rgba(226,232,240,0.95)',
                        }}
                      >
                        Email
                      </label>
                      <div className='position-relative'>
                        <LuMail
                          className='position-absolute top-50 translate-middle-y ms-3'
                          style={{
                            color: 'rgba(148,163,184,0.9)',
                            fontSize: '18px',
                          }}
                        />
                        <input
                          required
                          type='email'
                          placeholder='name@example.com'
                          value={formData.email}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                          className='form-control'
                          style={{
                            paddingLeft: '40px',
                            height: '40px',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '14px',
                            width: '100%',
                          }}
                        />
                      </div>
                    </div>

                    {/* Referral Code */}
                    <div className='mb-3'>
                      <label
                        className='d-block mb-2 fw-bold'
                        style={{
                          fontSize: '12px',
                          color: 'rgba(226,232,240,0.95)',
                        }}
                      >
                        Referral Code (Optional)
                      </label>
                      <div className='position-relative'>
                        <LuGift
                          className='position-absolute top-50 translate-middle-y ms-3'
                          style={{
                            color: 'rgba(148,163,184,0.9)',
                            fontSize: '18px',
                          }}
                        />
                        <input
                          type='text'
                          placeholder='Enter referral code'
                          value={formData.referralCode}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              referralCode: e.target.value,
                            }))
                          }
                          className='form-control'
                          style={{
                            paddingLeft: '40px',
                            height: '40px',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '14px',
                            width: '100%',
                          }}
                        />
                      </div>
                    </div>

                    {/* Checkbox */}
                    <div className='d-flex align-items-start gap-2 mb-4'>
                      <input
                        required
                        type='checkbox'
                        id='terms'
                        checked={formData.agreeTerms}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            agreeTerms: e.target.checked,
                          }))
                        }
                        className='mt-1 form-check-input'
                        style={{
                          backgroundColor: 'transparent',
                          borderColor: 'rgba(148,163,184,0.9)',
                          cursor: 'pointer',
                        }}
                      />
                      <label
                        htmlFor='terms'
                        style={{
                          fontSize: '13px',
                          color: 'rgba(148,163,184,0.9)',
                          lineHeight: '1.5',
                          cursor: 'pointer',
                        }}
                      >
                        I agree to the{' '}
                        <RouterLink
                          to='#'
                          onClick={() => window.open('./assets/Unitic_Exchange_Terms_Of_Use.pdf', '_blank')}
                          style={{ color: '#1da094', textDecoration: 'none' }}
                        >
                          Terms of Service
                        </RouterLink>{' '}
                        and{' '}
                        <RouterLink
                          to='#'
                          onClick={() => window.open('./assets/Unitic_Exchange_Privacy_Policy.pdf', '_blank')}
                          style={{ color: '#1da094', textDecoration: 'none' }}
                        >
                          Privacy Policy
                        </RouterLink>
                      </label>
                    </div>

                    {/* Submit Button */}
                    <button
                      type='submit'
                      className='btn w-100 fw-bold'
                      disabled={
                        !formData.agreeTerms || !formData.email || isLoading
                      }
                      style={{
                        backgroundColor: '#1da094',
                        color: 'white',
                        height: '40px',
                        borderRadius: '12px',
                        border: 'none',
                        fontSize: '15px',
                        boxShadow: '0 4px 20px rgba(29, 176, 161, 0.25)',
                        transition: 'all 0.2s',
                      }}
                    >
                      {isLoading ? (
                        <Spinner animation='border' size='sm' />
                      ) : (
                        'Create Account'
                      )}
                    </button>
                    <CaptchaModal
                      openCaptcha={openCaptcha}
                      handleOpenCaptcha={handleOpenCaptcha}
                      handleCloseCaptcha={handleCloseCaptcha}
                      captchaValue={captchaValue}
                    />
                    {/* Divider */}
                    <div className='position-relative my-4 text-center'>
                      <div
                        className='position-absolute w-100 top-50 start-0 border-top'
                        style={{ borderColor: 'rgba(255,255,255,0.1)' }}
                      ></div>
                      <span
                        className='position-relative px-2 text-uppercase fw-bold'
                        style={{
                          backgroundColor: '#0e1520',
                          color: 'rgba(148,163,184,0.7)',
                          fontSize: '11px',
                          borderRadius: '4px',
                        }}
                      >
                        Or sign up with
                      </span>
                    </div>

                    {/* Social Buttons */}
                    <div className='row g-3'>
                      <div className='col-6'>
                        <button
                          type='button'
                          className='btn w-100 d-flex align-items-center justify-content-center gap-2'
                          onClick={() => signWithGoogle()}
                          style={{
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            color: 'white',
                            height: '40px',
                            borderRadius: '12px',
                            fontSize: '14px',
                            fontWeight: '600',
                          }}
                        >
                          <svg
                            className='me-2'
                            width='20'
                            height='20'
                            viewBox='0 0 24 24'
                          >
                            <path
                              d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                              fill='#4285F4'
                            />
                            <path
                              d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                              fill='#34A853'
                            />
                            <path
                              d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                              fill='#FBBC05'
                            />
                            <path
                              d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                              fill='#EA4335'
                            />
                          </svg>
                          Google
                        </button>
                      </div>
                      <div className='col-6'>
                        <TelegramLogin
                          type={'register-web'}
                          handleAuthTele={handleAuthTele}
                        >
                          <button
                            type='button'
                            className='btn w-100 d-flex align-items-center justify-content-center gap-2'
                            style={{
                              backgroundColor: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.08)',
                              color: 'white',
                              height: '40px',
                              borderRadius: '12px',
                              fontSize: '14px',
                              fontWeight: '600',
                            }}
                          >
                            {' '}
                            <img src='./assets/socialLogos/telegram.svg' alt='' style={{ width: '20px', height: '20px' }} />{' '}
                            Telegram{' '}
                          </button>
                        </TelegramLogin>
                      </div>
                    </div>
                  </>
                ) : formData?.step === 2 ? (
                  <>
                    <div className='mx-auto text-center my-4 text-light'>
                      <div className='mx-auto text-center mt-3 mb-4'>
                        <p
                          className='text-start mb-3'
                          style={{
                            color: '#C8C8C8',
                            fontFamily:
                              'Roboto, "PingFang SC", -apple-system, BlinkMacSystemFont, "Microsoft YaHei"',
                          }}
                        >
                          OTP Was Send to {showEmail(formData.email)}
                        </p>
                        <OtpInput
                          id='otp'
                          name='otp'
                          value={formData.otp}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, otp: e }))
                          }
                          numInputs={6}
                          inputType='number'
                          shouldAutoFocus={true}
                          containerStyle={{
                            width: '100%',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            textAlign: 'center',
                          }}
                          inputStyle={{
                            borderRadius: '40px',
                            border: 'none',
                            width: '100%',
                            height: window.innerWidth < 500 ? '45px' : '52px',
                            outline: 'none',
                            fontWeight: '600',
                            fontSize: '22px',
                            fontFamily:
                              'Roboto, "PingFang SC", -apple-system, BlinkMacSystemFont, "Microsoft YaHei"',
                            textAlign: 'center',
                            background: '#1b202a',
                            color: 'white',
                          }}
                          renderSeparator={<span className='mx-2'></span>}
                          renderInput={(props) => <input {...props} />}
                        />

                        <p
                          className='text-end text-danger pt-3'
                          style={{
                            fontFamily:
                              'Roboto, "PingFang SC", -apple-system, BlinkMacSystemFont, "Microsoft YaHei"',
                            cursor: 'pointer',
                          }}
                          disabled={
                            isLoading || timer_resendOtp > 0 ? true : false
                          }
                          onClick={(e) => {
                            if (isLoading || timer_resendOtp > 0) return
                            handleSubmit(e, 1)
                          }}
                        >
                          Resend OTP{' '}
                          {timer_resendOtp > 0 ? (
                            <span className='timeLeft'>
                              ({timer_resendOtp})
                            </span>
                          ) : (
                            ''
                          )}
                        </p>
                      </div>
                    </div>
                    <button
                      type='submit'
                      className='btn w-100 fw-bold'
                      disabled={!formData.otp || isLoading}
                      style={{
                        backgroundColor: '#1da094',
                        color: 'white',
                        height: '40px',
                        borderRadius: '12px',
                        border: 'none',
                        fontSize: '15px',
                        boxShadow: '0 4px 20px rgba(29, 176, 161, 0.25)',
                        transition: 'all 0.2s',
                      }}
                    >
                      {isLoading ? (
                        <Spinner animation='border' size='sm' />
                      ) : (
                        'Submit OTP'
                      )}
                    </button>
                  </>
                ) : formData?.step === 3 ? (
                  <>
                    <div className='mb-3'>
                      <label
                        className='d-block mb-2 fw-bold'
                        style={{
                          fontSize: '12px',
                          color: 'rgba(226,232,240,0.95)',
                        }}
                      >
                        Username
                      </label>
                      <div className='position-relative'>
                        <FaRegUserCircle
                          className='position-absolute top-50 translate-middle-y ms-3'
                          style={{
                            color: 'rgba(148,163,184,0.9)',
                            fontSize: '18px',
                          }}
                        />
                        <input
                          required
                          type='text'
                          placeholder='name'
                          value={formData.username}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              username: e.target.value,
                            }))
                          }
                          className='form-control'
                          style={{
                            paddingLeft: '40px',
                            height: '40px',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '14px',
                            width: '100%',
                          }}
                        />
                      </div>
                    </div>
                    {formData.gAuth ? null : (
                      <div className='mb-3'>
                        <label
                          className='d-block mb-2 fw-bold'
                          style={{
                            fontSize: '12px',
                            color: 'rgba(226,232,240,0.95)',
                          }}
                        >
                          Password
                        </label>
                        <div className='position-relative'>
                          <CiLock
                            className='position-absolute top-50 translate-middle-y ms-3'
                            style={{
                              color: 'rgba(148,163,184,0.9)',
                              fontSize: '18px',
                            }}
                          />
                          <input
                            required
                            type={eye.pass ? 'text' : 'password'}
                            placeholder='password'
                            value={formData.password}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                password: e.target.value,
                              }))
                            }
                            className='form-control'
                            style={{
                              paddingLeft: '40px',
                              height: '40px',
                              backgroundColor: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.08)',
                              borderRadius: '12px',
                              color: 'white',
                              fontSize: '14px',
                              width: '100%',
                            }}
                          />
                          {eye.pass ? (
                            <FaRegEye
                              onClick={() =>
                                setEye((prev) => ({ ...prev, pass: false }))
                              }
                              className='position-absolute top-50 translate-middle-y end-0 me-3'
                              style={{
                                color: 'rgba(148,163,184,0.9)',
                                fontSize: '18px',
                                cursor: 'pointer',
                              }}
                            />
                          ) : (
                            <FaRegEyeSlash
                              onClick={() =>
                                setEye((prev) => ({ ...prev, pass: true }))
                              }
                              className='position-absolute top-50 translate-middle-y end-0 me-3'
                              style={{
                                color: 'rgba(148,163,184,0.9)',
                                fontSize: '18px',
                                cursor: 'pointer',
                              }}
                            />
                          )}
                        </div>
                      </div>
                    )}
                    {formData.gAuth ? null : (
                      <div className='mb-3'>
                        <label
                          className='d-block mb-2 fw-bold'
                          style={{
                            fontSize: '12px',
                            color: 'rgba(226,232,240,0.95)',
                          }}
                        >
                          Confirm Password
                        </label>
                        <div className='position-relative'>
                          <CiLock
                            className='position-absolute top-50 translate-middle-y ms-3'
                            style={{
                              color: 'rgba(148,163,184,0.9)',
                              fontSize: '18px',
                            }}
                          />
                          <input
                            required
                            type={eye.coPass ? 'text' : 'password'}
                            placeholder='confirm password'
                            value={formData.confirmPassword}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                confirmPassword: e.target.value,
                              }))
                            }
                            className='form-control'
                            style={{
                              paddingLeft: '40px',
                              height: '40px',
                              backgroundColor: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.08)',
                              borderRadius: '12px',
                              color: 'white',
                              fontSize: '14px',
                              width: '100%',
                            }}
                          />
                          {eye.coPass ? (
                            <FaRegEye
                              onClick={() =>
                                setEye((prev) => ({ ...prev, coPass: false }))
                              }
                              className='position-absolute top-50 translate-middle-y end-0 me-3'
                              style={{
                                color: 'rgba(148,163,184,0.9)',
                                fontSize: '18px',
                                cursor: 'pointer',
                              }}
                            />
                          ) : (
                            <FaRegEyeSlash
                              onClick={() =>
                                setEye((prev) => ({ ...prev, coPass: true }))
                              }
                              className='position-absolute top-50 translate-middle-y end-0 me-3'
                              style={{
                                color: 'rgba(148,163,184,0.9)',
                                fontSize: '18px',
                                cursor: 'pointer',
                              }}
                            />
                          )}
                        </div>
                      </div>
                    )}
                    <div className='mb-3'>
                      <label
                        className='d-block mb-2 fw-bold'
                        style={{
                          fontSize: '12px',
                          color: 'rgba(226,232,240,0.95)',
                        }}
                      >
                        Phone
                      </label>
                      <div style={{ marginTop: '-5px' }}>
                        <PhoneInput
                          containerClass='container'
                          containerStyle={{
                            width: '100%',
                            border: '0',
                            borderRadius: '0',
                            padding: '5px 0px',
                          }}
                          inputStyle={{
                            paddingLeft: '40px',
                            height: '40px',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '14px',
                            width: '100%',
                          }}
                          specialLabel={false}
                          buttonStyle={{
                            backgroundColor: 'transparent',
                            border: 'none',
                          }}
                          dropdownStyle={{
                            backgroundColor: '#151515',
                            textAlign: 'left',
                            color: 'white',
                          }}
                          searchStyle={{ color: 'white' }}
                          required
                          inputProps={{
                            name: 'phone',
                            required: true,
                            autoFocus: true,
                          }}
                          international
                          country={'in'}
                          className='form-control handleModalBody'
                          countryCallingCodeEditable={true}
                          value={formData.phoneno}
                          onChange={setphoneno}
                          placeholder='Enter phone number'
                          autoComplete='off'
                        />
                      </div>
                    </div>
                    {formData.gAuth ? (
                      <div className='mb-3'>
                        <label
                          className='d-block mb-2 fw-bold'
                          style={{
                            fontSize: '12px',
                            color: 'rgba(226,232,240,0.95)',
                          }}
                        >
                          Referral Code (Optional)
                        </label>
                        <div className='position-relative'>
                          <LuGift
                            className='position-absolute top-50 translate-middle-y ms-3'
                            style={{
                              color: 'rgba(148,163,184,0.9)',
                              fontSize: '18px',
                            }}
                          />
                          <input
                            type='text'
                            placeholder='Enter referral code'
                            value={formData.referralCode}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                referralCode: e.target.value,
                              }))
                            }
                            className='form-control'
                            style={{
                              paddingLeft: '40px',
                              height: '40px',
                              backgroundColor: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.08)',
                              borderRadius: '12px',
                              color: 'white',
                              fontSize: '14px',
                              width: '100%',
                            }}
                          />
                        </div>
                      </div>
                    ) : null}
                    <button
                      type='submit'
                      className='btn w-100 fw-bold'
                      disabled={
                        isLoading ||
                        !formData.username ||
                        !phoneno ||
                        (!formData.gAuth &&
                          (!formData.password || !formData.confirmPassword))
                      }
                      style={{
                        backgroundColor: '#1da094',
                        color: 'white',
                        height: '40px',
                        borderRadius: '12px',
                        border: 'none',
                        fontSize: '15px',
                        boxShadow: '0 4px 20px rgba(29, 176, 161, 0.25)',
                        transition: 'all 0.2s',
                      }}
                    >
                      {isLoading ? (
                        <Spinner animation='border' size='sm' />
                      ) : (
                        'Submit Details'
                      )}
                    </button>
                  </>
                ) : formData?.step === 1 && formData?.teleAuth ? (
                  <>
                    <div className='mb-3'>
                      <label
                        className='d-block mb-2 fw-bold'
                        style={{
                          fontSize: '12px',
                          color: 'rgba(226,232,240,0.95)',
                        }}
                      >
                        Email
                      </label>
                      <div className='position-relative'>
                        <LuMail
                          className='position-absolute top-50 translate-middle-y ms-3'
                          style={{
                            color: 'rgba(148,163,184,0.9)',
                            fontSize: '18px',
                          }}
                        />
                        <input
                          required
                          type='email'
                          placeholder='name@example.com'
                          value={formData.email}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                          className='form-control'
                          style={{
                            paddingLeft: '40px',
                            height: '40px',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '14px',
                            width: '100%',
                          }}
                        />
                      </div>
                    </div>
                    <div className='mb-3'>
                      <label
                        className='d-block mb-2 fw-bold'
                        style={{
                          fontSize: '12px',
                          color: 'rgba(226,232,240,0.95)',
                        }}
                      >
                        Phone
                      </label>
                      <div style={{ marginTop: '-5px' }}>
                        <PhoneInput
                          containerClass='container'
                          containerStyle={{
                            width: '100%',
                            border: '0',
                            borderRadius: '0',
                            padding: '5px 0px',
                          }}
                          inputStyle={{
                            paddingLeft: '40px',
                            height: '40px',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '14px',
                            width: '100%',
                          }}
                          specialLabel={false}
                          buttonStyle={{
                            backgroundColor: 'transparent',
                            border: 'none',
                          }}
                          dropdownStyle={{
                            backgroundColor: '#151515',
                            textAlign: 'left',
                            color: 'white',
                          }}
                          searchStyle={{ color: 'white' }}
                          required
                          inputProps={{
                            name: 'phone',
                            required: true,
                            autoFocus: true,
                          }}
                          international
                          country={'in'}
                          className='form-control handleModalBody'
                          countryCallingCodeEditable={true}
                          value={formData.phoneno}
                          onChange={setphoneno}
                          placeholder='Enter phone number'
                          autoComplete='off'
                        />
                      </div>
                    </div>
                    <div className='mb-3'>
                      <label
                        className='d-block mb-2 fw-bold'
                        style={{
                          fontSize: '12px',
                          color: 'rgba(226,232,240,0.95)',
                        }}
                      >
                        Referral Code (Optional)
                      </label>
                      <div className='position-relative'>
                        <LuGift
                          className='position-absolute top-50 translate-middle-y ms-3'
                          style={{
                            color: 'rgba(148,163,184,0.9)',
                            fontSize: '18px',
                          }}
                        />
                        <input
                          type='text'
                          placeholder='Enter referral code'
                          value={formData.referralCode}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              referralCode: e.target.value,
                            }))
                          }
                          className='form-control'
                          style={{
                            paddingLeft: '40px',
                            height: '40px',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '14px',
                            width: '100%',
                          }}
                        />
                      </div>
                    </div>
                    <button
                      type='submit'
                      className='btn w-100 fw-bold'
                      disabled={isLoading || !formData.email || !phoneno}
                      style={{
                        backgroundColor: '#1da094',
                        color: 'white',
                        height: '40px',
                        borderRadius: '12px',
                        border: 'none',
                        fontSize: '15px',
                        boxShadow: '0 4px 20px rgba(29, 176, 161, 0.25)',
                        transition: 'all 0.2s',
                      }}
                    >
                      {isLoading ? (
                        <Spinner animation='border' size='sm' />
                      ) : (
                        'Submit Details'
                      )}
                    </button>
                  </>
                ) : null}
              </form>
              <p
                className='mt-4 text-center mb-0'
                style={{ fontSize: '13.5px', color: '#94A3B8' }}
              >
                Already have an account?{' '}
                <RouterLink
                  to='/login'
                  className='fw-bold text-decoration-none'
                  style={{ color: '#1da094' }}
                >
                  Sign in
                </RouterLink>
              </p>
            </div>
          </div>
        </div>

        {<Splash open={successPopup} />}
      </Box>
    </>
  )
}
